import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedButton from '../../components/Shared/SharedButton';
import DropdownComponent from '../../components/Shared/DropdownComponent';
import TextInput from '../../components/Shared/TextInput';
import PageHeader from '../../components/Shared/PageHeader';
import DatePicker from '../../components/Shared/DatePicker';
import { FaCheckDouble } from 'react-icons/fa6';
import CustomSearchDropdown from '../../components/Shared/CustomSearchDropdown';
import { getAllEmployees } from '../../store/slices/EmployeeSlice';
import { CreateNewAllowance, UpdateAllowance } from '../../store/slices/HrSlice';



export default function CreateAllowance() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector(state => state.auth);
  const { isLoading: loaiding } = useSelector(state => state?.hr);
  const { allEmployees } = useSelector(state => state.employee);

  const listEmployees = allEmployees?.data
    ? allEmployees.data.map(employee => ({
      id: employee.id,
      name: `${employee.first_name} ${employee.last_name}`
    }))
    : [];

  useEffect(() => {
    dispatch(getAllEmployees());
  }, [dispatch]);

  const location = useLocation();
  const { editingAllowance } = location.state || {};

  const initialValues = {
    employee_id: editingAllowance?.employee_id || '',
    allowance_type: editingAllowance?.allowance_type || '',
    amount: editingAllowance?.amount || 0,
    effective_date: editingAllowance?.effective_date ? new Date(editingAllowance.effective_date) : null,
    branch_id: editingAllowance?.branch_id || user?.branch_id,
    business_id: editingAllowance?.business_id || user?.business_id
  };

  const validationSchema = Yup.object({
    employee_id: Yup.number().required(t('required')),
    allowance_type: Yup.string().required(t('required')),
    amount: Yup.number().min(0, `${t('amount')}${t('mustBePositive')}`),
    effective_date: Yup.date().required(t('required'))
  });

  const allowance_type_option = [
    { value: 'Housing', label: 'Housing' },
    { value: 'Travel', label: 'Travel' },
    { value: 'Bonus', label: 'Bonus' },
    { value: 'Food', label: 'Food' }
  ];

  const onSubmit = async (values, { resetForm }) => {
    try {
      const formattedData = {
        ...values,
        effective_date: values.effective_date ? new Date(values.effective_date).toISOString().split('T')[0] : '',
      };

      const resultAction = editingAllowance?.id
        ? await dispatch(UpdateAllowance({ ...formattedData, id: editingAllowance?.id }))
        : await dispatch(CreateNewAllowance(formattedData));

      if (UpdateAllowance.fulfilled.match(resultAction) || CreateNewAllowance.fulfilled.match(resultAction)) {
        resetForm();
        navigate(-1);
      }
    } catch (error) {
      console.error('Error submitting allowance:', error);
    }
  };

  return (
    <div className="flex flex-col w-full gap-4 max-h-[calc(100vh-150px)]">
      <PageHeader
        title={t('Create Allowance')}
        showBack
        onBack={() => navigate(-1)}
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full'>
              <CustomSearchDropdown
                name='employee_id'
                placeholder={`${t('select')} ${t('employee')}`}
                options={listEmployees}
                label={`${t('employee')} ${t('name')}`}
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={'!w-full !border-gray-300 !bg-gray-100 !h-[30px]'}
              />

              <DropdownComponent
                label={t('allowance_type')}
                placeholder={`${t('select')} ${t('allowance_type')}`}
                name={'allowance_type'}
                options={allowance_type_option}
                className='!w-full'
              />

              <DatePicker
                label={t('effective_date')}
                name={'effective_date'}
                placeholder={`${t('select')} ${t('effective_date')}`}
                className='!w-full'
              />

              <TextInput
                type={'number'}
                label={t('amount')}
                name={'amount'}
                placeholder={`${t('enter')} ${t('amount')}`}
                className='!p-1.5'
              />
            </div>

            <div className='flex justify-end gap-2'>
              <SharedButton
                type='button'
                icon='pi pi-times'
                label={t('Cancel')}
                buttonType='reset'
                onClick={() => {
                  navigate(-1);
                }}
              />
              <SharedButton
                type='submit'
                icon={<FaCheckDouble size={12} className='me-2' />}
                loading={loaiding}
                disabled={loaiding}
                label={t('Submit')}
                className='submit-btn'
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}